/**
 * Formato para pintar.
 *
 * ## Eden devuelve `Date`, no texto
 *
 * Es la trampa de la casa y conviene tenerla clara: un campo con
 * `format: 'date'` o `'date-time'` en el esquema del servidor llega al cliente
 * **ya deserializado a `Date`**. `{alumno.joined_on}` en un JSX no es un texto:
 * es un objeto, y React tira *«Objects are not valid as a React child»* y deja la
 * pantalla en blanco. Toda fecha pasa por aquí.
 *
 * Y el segundo filo: `joined_on` es una fecha **sin hora** —`2026-09-16`— que Eden
 * interpreta como medianoche UTC. Con `toISOString().slice(0, 10)` eso vuelve a
 * salir bien en España, pero en cualquier zona al oeste de Greenwich sale **el día
 * anterior**. Por eso se formatea en local y no se recorta el ISO.
 */

const DIA = new Intl.DateTimeFormat('es-ES', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const DIA_Y_HORA = new Intl.DateTimeFormat('es-ES', {
  dateStyle: 'short',
  timeStyle: 'short',
});

/** Una fecha sin hora. `null` sale como raya, que ocupa sitio y se lee. */
export const fecha = (valor: Date | string | null | undefined): string => {
  if (!valor) return '—';
  const d = valor instanceof Date ? valor : new Date(valor);
  return Number.isNaN(d.getTime()) ? '—' : DIA.format(d);
};

export const fechaYHora = (valor: Date | string | null | undefined): string => {
  if (!valor) return '—';
  const d = valor instanceof Date ? valor : new Date(valor);
  return Number.isNaN(d.getTime()) ? '—' : DIA_Y_HORA.format(d);
};

/**
 * Dinero. **Céntimos enteros hasta el último momento.**
 *
 * Dividir y volver a multiplicar en coma flotante es lo que acumula descuadres:
 * `0.1 + 0.2` no es `0.3`, y en una remesa de 150 recibos eso se nota.
 */
export const euros = (centimos: number): string =>
  new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
  }).format(centimos / 100);

/**
 * Un contador con su nombre concordado: `cuenta(1, 'sede', 'sedes')` → «1 sede».
 *
 * ## Existe porque la misma falta se colaba una vez por pantalla
 *
 * «1 recibos emitidos sin cobrar» en la previsión de caja, «1 ya estaban dentro» en
 * el arranque de un club, «hasta 1 sedes» en los planes. Tres en el mismo día, y
 * siempre igual: se interpola el contador delante del nombre y el plural se escribe
 * una sola vez, porque casi siempre son varios.
 *
 * Lo que la hace difícil de ver es que **con datos de verdad casi nunca se nota**: un
 * club tiene tres sedes y catorce recibos. El uno aparece en el club nuevo, que es
 * justo el que va a mirar la pantalla por primera vez.
 *
 * No intenta pluralizar sola —`sede` → `sedes` es fácil, `lápiz` → `lápices` no— y no
 * hace falta: quien escribe la frase sabe las dos palabras.
 *
 * El cero lleva plural, que es lo correcto en español: «0 sedes».
 */
export const cuenta = (n: number, singular: string, plural: string): string =>
  `${n} ${n === 1 ? singular : plural}`;

/**
 * Un número con decimales, en español.
 *
 * ## Existe porque no existía
 *
 * Sin una ayuda de la casa, cada pantalla que tenía que pintar un decimal escribió
 * su `toFixed(1)`, y **`toFixed` no sabe de idiomas**: escribe siempre el punto
 * inglés. Así que «Horas de entrenador» decía `6.5` dos filas por debajo de un
 * `50,00 €`, y la ficha de examen `10.00`.
 *
 * Se vio el 18-09-2026, y **se vio por los ceros**: con datos de verdad un `6.5`
 * pasa por número y no llama la atención, pero un `0.0` en una base recién sembrada
 * sí. Es el mismo bicho que el `text-transform: capitalize` de los meses: `Intl`
 * estaba ahí y no se usó.
 *
 * `aEuros` de `club/cuotas/tipos.ts` **no** pasa por aquí, y a propósito: eso rellena
 * un `<input>` que luego se relee, y el separador de miles rompería su propio lector.
 */
export const numero = (valor: number, decimales = 1): string =>
  new Intl.NumberFormat('es-ES', {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  }).format(valor);

/**
 * Una fecha → lo que entiende un `<input type="date">`, que es `YYYY-MM-DD`.
 *
 * **No es `toISOString().slice(0, 10)`.** Eden entrega una fecha sin hora como
 * medianoche UTC, así que recortar el ISO acierta en España y saca el día anterior
 * al oeste de Greenwich. Se arma a mano con los componentes locales, como en
 * `club/cuotas/pausas.ts`.
 *
 * Cadena vacía cuando no hay fecha: es lo que vacía el campo sin que React se
 * queje de un `value` nulo.
 */
export function fechaParaInput(
  valor: Date | string | null | undefined
): string {
  if (!valor) return '';
  const d = valor instanceof Date ? valor : new Date(valor);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
}
